import React from 'react';
import Icon from '../../../components/AppIcon';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PerformanceMonitoringPanel = ({ performanceData, systemResources }) => {
  const resourceCards = [
    {
      label: 'استخدام المعالج',
      value: systemResources?.cpuUsage,
      icon: 'Cpu',
      color: 'text-[var(--color-primary)]',
      bgColor: 'bg-[var(--color-primary)]/10',
      unit: '%',
    },
    {
      label: 'استخدام الذاكرة',
      value: systemResources?.memoryUsage,
      icon: 'HardDrive',
      color: 'text-[var(--color-accent)]',
      bgColor: 'bg-[var(--color-accent)]/10',
      unit: '%',
    },
    {
      label: 'مساحة التخزين',
      value: systemResources?.diskUsage,
      icon: 'Database',
      color: 'text-[var(--color-secondary)]',
      bgColor: 'bg-[var(--color-secondary)]/10',
      unit: '%',
    },
    {
      label: 'استخدام الشبكة',
      value: systemResources?.networkUsage,
      icon: 'Wifi',
      color: 'text-[var(--color-success)]',
      bgColor: 'bg-[var(--color-success)]/10',
      unit: 'MB/s',
    },
  ];

  const getStatusColor = (value) => {
    if (value < 50) return 'text-[var(--color-success)]';
    if (value < 80) return 'text-[var(--color-warning)]';
    return 'text-[var(--color-error)]';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {resourceCards?.map((resource, index) => (
          <div
            key={index}
            className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-4 md:p-6 shadow-warm"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg ${resource?.bgColor}`}>
                <Icon name={resource?.icon} size={24} className={resource?.color} />
              </div>
              <span className={`text-xs font-medium ${getStatusColor(resource?.value)}`}>
                {resource?.value < 50 ? 'طبيعي' : resource?.value < 80 ? 'متوسط' : 'مرتفع'}
              </span>
            </div>
            <h3 className={`text-2xl md:text-3xl font-heading font-bold mb-2 ${getStatusColor(resource?.value)}`}>
              {resource?.value}{resource?.unit}
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">{resource?.label}</p>
            <div className="mt-3 w-full bg-[var(--color-muted)] rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  resource?.value < 50 ? 'bg-[var(--color-success)]' :
                  resource?.value < 80 ? 'bg-[var(--color-warning)]': 'bg-[var(--color-error)]'
                }`}
                style={{ width: `${resource?.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-4 md:p-6 shadow-warm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-[var(--color-foreground)]">
                زمن الاستجابة
              </h3>
              <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
                متوسط زمن استجابة النظام (مللي ثانية)
              </p>
            </div>
            <Icon name="Activity" size={24} color="var(--color-primary)" />
          </div>
          <div className="w-full h-64" aria-label="Response Time Chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={performanceData?.responseTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="time" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="var(--color-primary)"
                  fill="var(--color-primary)"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-4 md:p-6 shadow-warm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-[var(--color-foreground)]">
                المستخدمون المتزامنون
              </h3>
              <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
                عدد المستخدمين النشطين في النظام
              </p>
            </div>
            <Icon name="Users" size={24} color="var(--color-success)" />
          </div>
          <div className="w-full h-64" aria-label="Concurrent Users Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData?.concurrentUsers}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="time" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" fill="var(--color-success)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-4 md:p-6 shadow-warm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-heading font-semibold text-[var(--color-foreground)]">
              إحصائيات الأداء
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
              مؤشرات الأداء الرئيسية للنظام
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Zap" size={20} color="var(--color-warning)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">متوسط وقت التحميل</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[var(--color-foreground)]">
              {performanceData?.avgLoadTime}ms
            </p>
          </div>

          <div className="p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">معدل النجاح</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[var(--color-foreground)]">
              {performanceData?.successRate}%
            </p>
          </div>

          <div className="p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">وقت التشغيل</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[var(--color-foreground)]">
              {performanceData?.uptime}%
            </p>
          </div>

          <div className="p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">معدل الأخطاء</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[var(--color-foreground)]">
              {performanceData?.errorRate}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMonitoringPanel;